<template>
  <Drawer
    :visible="visible"
    position="right"
    class="constraint-detail-drawer"
    :pt="drawerPt"
    :style="{ width: '480px', maxWidth: '100vw' }"
    data-testid="constraint-detail-drawer"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #header>
      <div v-if="constraint" class="constraint-detail-drawer__header">
        <div class="constraint-detail-drawer__header-badges">
          <span
            class="constraint-detail-drawer__status-pill"
            :class="`constraint-detail-drawer__status-pill--${statusKey}`"
          >
            {{ statusPillLabel }}
          </span>
          <span class="constraint-detail-drawer__priority">
            <span
              class="constraint-detail-drawer__priority-dot"
              :class="`constraint-detail-drawer__priority-dot--${priorityKey}`"
            />
            <span :class="`constraint-detail-drawer__priority-text--${priorityKey}`">
              {{ priorityLabel }}
            </span>
          </span>
        </div>
        <h2 class="constraint-detail-drawer__title">{{ title }}</h2>
      </div>
    </template>

    <div v-if="constraint" class="constraint-detail-drawer__body">
      <div class="constraint-detail-drawer__meta-grid">
        <div>
          <p class="constraint-detail-drawer__meta-label">
            {{ $t('UserProjects.constraints.drawer.category') }}
          </p>
          <span class="constraint-detail-drawer__category-pill">
            {{ categoryLabel }}
          </span>
        </div>
        <div>
          <p class="constraint-detail-drawer__meta-label">
            {{ $t('UserProjects.constraints.drawer.owner') }}
          </p>
          <div class="constraint-detail-drawer__owner">
            <span class="constraint-detail-drawer__avatar">{{ ownerInitials }}</span>
            <span>{{ constraint.ownerDisplayName || '—' }}</span>
          </div>
        </div>
        <div>
          <p class="constraint-detail-drawer__meta-label">
            {{ $t('UserProjects.constraints.drawer.created') }}
          </p>
          <span>{{ formatConstraintDate(constraint.date_created) }}</span>
        </div>
        <div>
          <p class="constraint-detail-drawer__meta-label">
            {{ $t('UserProjects.constraints.drawer.milestone') }}
          </p>
          <span v-if="constraint.milestoneTitle" class="constraint-detail-drawer__milestone">
            {{ constraint.milestoneTitle }}
          </span>
          <span v-else>—</span>
        </div>
      </div>

      <div>
        <p class="constraint-detail-drawer__section-label">
          {{ $t('UserProjects.constraints.drawer.description') }}
        </p>
        <p class="constraint-detail-drawer__description">{{ constraint.description }}</p>
      </div>

      <div
        v-if="constraint.commitmentId"
        class="constraint-detail-drawer__cause-banner"
      >
        <i class="pi pi-link" aria-hidden="true" />
        <span>{{ $t('UserProjects.constraints.drawer.fromCause') }}</span>
      </div>

      <div>
        <p class="constraint-detail-drawer__section-label">
          {{ $t('UserProjects.constraints.drawer.solution') }}
        </p>
        <Textarea
          v-model="solutionText"
          class="constraint-detail-drawer__textarea w-full"
          rows="3"
          auto-resize
          data-testid="constraint-drawer-solution"
        />
        <p class="constraint-detail-drawer__hint">
          {{ $t('UserProjects.constraints.drawer.solutionHint') }}
        </p>
        <div class="constraint-detail-drawer__solution-actions">
          <span
            v-if="solutionSaved"
            class="constraint-detail-drawer__saved"
          >
            <i class="pi pi-check-circle" aria-hidden="true" />
            {{ $t('UserProjects.constraints.drawer.solutionSaved') }}
          </span>
          <button
            v-if="solutionChanged"
            type="button"
            class="constraint-detail-drawer__save-solution-btn"
            data-testid="constraint-drawer-save-solution"
            @click="saveSolution"
          >
            {{ $t('UserProjects.constraints.drawer.saveSolution') }}
          </button>
        </div>
      </div>
    </div>

    <template #footer>
      <div v-if="constraint" class="constraint-detail-drawer__footer">
        <button
          v-if="isEditable"
          type="button"
          class="constraint-detail-drawer__resolve-btn"
          data-testid="constraint-drawer-resolve"
          @click="$emit('resolve', constraint)"
        >
          <i class="pi pi-check-circle" aria-hidden="true" />
          {{ $t('UserProjects.constraints.drawer.markResolved') }}
        </button>
        <span v-else />
        <button
          v-if="isEditable"
          type="button"
          class="constraint-detail-drawer__edit-btn"
          data-testid="constraint-drawer-edit"
          @click="$emit('edit', constraint)"
        >
          {{ $t('UserProjects.constraints.drawer.edit') }}
        </button>
      </div>
    </template>
  </Drawer>
</template>

<script>
import Drawer from 'primevue/drawer';
import Textarea from 'primevue/textarea';
import {
  displayConstraintTitle,
  isActiveConstraintStatus,
} from '@/utils/constraintFormMapping';
import {
  formatConstraintDate,
  getConstraintPriority,
  userInitials,
} from '@/utils/constraintDisplay';

export default {
  name: 'ConstraintDetailDrawer',
  components: {
    Drawer,
    Textarea,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    constraint: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:visible', 'resolve', 'edit', 'save-solution'],
  data() {
    return {
      solutionText: '',
      initialSolutionText: '',
      solutionSaved: false,
    };
  },
  computed: {
    title() {
      return this.constraint ? displayConstraintTitle(this.constraint) : '';
    },
    statusKey() {
      if (this.constraint?.status === 'resolved') return 'resolved';
      if (this.constraint?.status === 'in_progress') return 'in_progress';
      return 'open';
    },
    statusPillLabel() {
      return this.$t(`UserProjects.constraints.drawer.status.${this.statusKey}`);
    },
    priorityMeta() {
      return this.constraint ? getConstraintPriority(this.constraint) : null;
    },
    priorityKey() {
      return this.priorityMeta?.key || 'medium';
    },
    priorityLabel() {
      return this.$t(this.priorityMeta?.labelKey || 'UserProjects.overview.constraints.priority.medium');
    },
    isEditable() {
      return isActiveConstraintStatus(this.constraint?.status);
    },
    solutionChanged() {
      return this.solutionText !== this.initialSolutionText;
    },
    categoryLabel() {
      if (!this.constraint?.category) return '—';
      const key = this.constraint.category;
      const translated = this.$t(`UserProjects.constraintDialog.categories.${key}`);
      if (translated !== `UserProjects.constraintDialog.categories.${key}`) {
        return translated;
      }
      return this.$t(`ProjectManagement.commitments.cause.categories.${key}`);
    },
    ownerInitials() {
      return userInitials(this.constraint?.ownerDisplayName);
    },
    drawerPt() {
      return {
        root: {
          'data-testid': 'constraint-detail-drawer',
        },
      };
    },
  },
  watch: {
    constraint: {
      immediate: true,
      handler(nextConstraint) {
        this.solutionText = nextConstraint?.solutionText || '';
        this.initialSolutionText = this.solutionText;
        this.solutionSaved = false;
      },
    },
  },
  methods: {
    formatConstraintDate,
    saveSolution() {
      if (!this.constraint?.id) return;
      this.$emit('save-solution', {
        id: this.constraint.id,
        solutionText: this.solutionText,
      });
      this.initialSolutionText = this.solutionText;
      this.solutionSaved = true;
      window.setTimeout(() => {
        this.solutionSaved = false;
      }, 2000);
    },
  },
};
</script>

<style scoped>
.constraint-detail-drawer__header-badges {
  margin-bottom: 0.5rem;
}

.constraint-detail-drawer__status-pill {
  display: inline-flex;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.constraint-detail-drawer__status-pill--in_progress {
  background: #fef9c3;
  color: #854d0e;
}

.constraint-detail-drawer__status-pill--open {
  background: #f2f2f2;
  color: #6b6b6b;
}

.constraint-detail-drawer__status-pill--resolved {
  background: #dcfce7;
  color: #166534;
}

.constraint-detail-drawer__priority {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 12px;
  font-weight: 500;
}

.constraint-detail-drawer__priority-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
}

.constraint-detail-drawer__priority-dot--high {
  background: #ef4444;
}

.constraint-detail-drawer__priority-dot--medium {
  background: #eab308;
}

.constraint-detail-drawer__priority-dot--low {
  background: #ababab;
}

.constraint-detail-drawer__priority-text--high {
  color: #991b1b;
}

.constraint-detail-drawer__priority-text--medium {
  color: #854d0e;
}

.constraint-detail-drawer__priority-text--low {
  color: #6b6b6b;
}

.constraint-detail-drawer__solution-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.constraint-detail-drawer__saved {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 12px;
  color: #16a34a;
}

.constraint-detail-drawer__save-solution-btn {
  margin-left: auto;
  padding: 0.375rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.constraint-detail-drawer__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-dark);
}

.constraint-detail-drawer__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.constraint-detail-drawer__meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 10px;
  background: #f9f9f9;
  font-size: 13px;
  color: var(--color-dark);
}

.constraint-detail-drawer__meta-label {
  margin: 0 0 0.25rem;
  font-size: 11px;
  font-weight: 500;
  color: #6b6b6b;
}

.constraint-detail-drawer__category-pill {
  display: inline-flex;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: #f2f2f2;
  color: #6b6b6b;
  font-size: 11px;
  font-weight: 500;
}

.constraint-detail-drawer__owner {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.constraint-detail-drawer__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: var(--color-orange);
  color: #fff;
  font-size: 9px;
  font-weight: 600;
}

.constraint-detail-drawer__milestone {
  color: var(--color-orange);
  font-weight: 500;
}

.constraint-detail-drawer__section-label {
  margin: 0 0 0.375rem;
  font-size: 13px;
  font-weight: 500;
  color: #6b6b6b;
}

.constraint-detail-drawer__description {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-dark);
}

.constraint-detail-drawer__cause-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 10px;
  background: #fef9c3;
  border-left: 3px solid #eab308;
  color: #854d0e;
  font-size: 12px;
  font-weight: 500;
}

.constraint-detail-drawer__hint {
  margin: 0.375rem 0 0;
  font-size: 12px;
  color: #ababab;
}

.constraint-detail-drawer__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.constraint-detail-drawer__resolve-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: #16a34a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.constraint-detail-drawer__resolve-btn:hover {
  background: rgba(22, 163, 74, 0.05);
  border-color: rgba(22, 163, 74, 0.2);
}

.constraint-detail-drawer__edit-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 10px;
  background: var(--accent-gradient);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
</style>

<style>
.constraint-detail-drawer.p-drawer {
  font-family: var(--font-family, 'AsenPro', sans-serif);
}

.constraint-detail-drawer .p-drawer-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.constraint-detail-drawer .p-drawer-content {
  padding: 1.25rem 1.5rem;
}

.constraint-detail-drawer .p-drawer-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.constraint-detail-drawer .constraint-detail-drawer__textarea.p-textarea {
  border-radius: 10px;
  font-size: 14px;
}
</style>
